<script setup>
import {useUsersStore, useUserGroupsStore, useTagsStore} from "@/stores"
import { mediaUrl } from '@core/utils/mediaUrl'

const usersStore = useUsersStore()
const userGroupsStore = useUserGroupsStore()
const tagsStore = useTagsStore()

const myUser = computed(() => usersStore.myUser)
const loginDialogMode = computed({
  get: () => usersStore.loginDialogMode,
  set: val => usersStore.loginDialogMode = val
})

const partners = ref([])
const loadingPartners = ref(true)

const isConnectionDialogVisible = ref(false)
const isConfirmationDialogVisible = ref(false)
const selectedPartner = ref(null)
const isRequestingConnection = ref(false)

onMounted(async () => {
  try {
    const [partnerGroups, tagCountsByName] = await Promise.all([
      userGroupsStore.getPartnerGroups(),
      tagsStore.getPartnerTagCounts()
    ])
    
    if (partnerGroups && partnerGroups.length > 0) {
      partners.value = partnerGroups.map(group => {
        const memberCount = tagCountsByName[group.name] || 0
        
        return {
          id: group.id,
          name: group.name,
          logoImageId: group.profileImageId,
          url: group.groupUrl,
          categoryName: group.categoryName,
          description: group.groupDescription || 'No description available.',
          publicId: group.publicId,
          type: group.type,
          memberCount: memberCount
        }
      }).sort((a, b) => b.memberCount - a.memberCount)
    }
  } catch (error) {
    console.error('Error loading partners:', error)
  } finally {
    loadingPartners.value = false
  }
})

const truncateDescription = (text, maxLength = 250) => {
  if (!text || text.length <= maxLength) return text
  
  return text.substring(0, maxLength).trim() + '...'
}

const handleRequestConnection = partner => {
  if (!myUser.value || myUser.value.userLevel === 'dummy') {
    loginDialogMode.value = 'activate'
    
    return
  }
  
  selectedPartner.value = partner
  isConnectionDialogVisible.value = true
}

const onConfirmConnection = async () => {
  isRequestingConnection.value = true
  try {
    await userGroupsStore.requestPartnerConnection(selectedPartner.value.id)
    isConnectionDialogVisible.value = false
    isConfirmationDialogVisible.value = true
  } catch (error) {
    console.error('Error requesting connection:', error)
    alert('There was an error sending your connection request. Please try again.')
  } finally {
    isRequestingConnection.value = false
  }
}

const onCancelConnection = () => {
  isConnectionDialogVisible.value = false
  selectedPartner.value = null
}
</script>

<template>
  <div>
    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n4"
    >
      <div class="back-button-container">
        <RouterLink
          :to="`/m/${myUser?.profileId}`"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Profile</span>
        </RouterLink>
      </div>
    </div>

    <VDialog
      v-model="isConnectionDialogVisible"
      max-width="500"
    >
      <VCard class="text-center px-10 py-6">
        <VCardText>
          <div class="logo-container-dialog mb-4">
            <VImg
              v-if="selectedPartner?.logoImageId"
              :src="mediaUrl(selectedPartner.logoImageId, 'small')"
              :alt="selectedPartner.name"
              contain
              max-height="80"
              max-width="200"
              class="mx-auto"
            />
            <div
              v-else
              class="logo-placeholder-dialog"
            >
              {{ selectedPartner?.name }}
            </div>
          </div>

          <h6 class="text-lg font-weight-medium mb-2">
            Request connection to {{ selectedPartner?.name }}
          </h6>
        </VCardText>

        <VCardActions class="align-center justify-center gap-2">
          <VBtn
            variant="elevated"
            color="primary"
            :loading="isRequestingConnection"
            :disabled="isRequestingConnection"
            @click="onConfirmConnection"
          >
            Yes, please connect
          </VBtn>

          <VBtn
            color="secondary"
            variant="tonal"
            :disabled="isRequestingConnection"
            @click="onCancelConnection"
          >
            Cancel
          </VBtn>
        </VCardActions>
      </VCard>
      <VBtn
        icon="$close"
        size="large"
        variant="text"
        color="secondary"
        class="dialog-close-btn"
        @click="onCancelConnection"
      >
        <VIcon>mdi-close</VIcon>
      </VBtn>
    </VDialog>

    <VDialog
      v-model="isConfirmationDialogVisible"
      max-width="500"
    >
      <VCard class="text-center px-10 py-6">
        <VCardText>
          <VBtn
            icon
            variant="outlined"
            color="success"
            class="mb-4"
            style="pointer-events: none;"
          >
            <VIcon size="32">
              mdi-check
            </VIcon>
          </VBtn>

          <h6 class="text-lg font-weight-medium">
            Great, we'll connect you ASAP
          </h6>
        </VCardText>

        <VCardActions class="align-center justify-center">
          <VBtn
            variant="elevated"
            color="primary"
            @click="isConfirmationDialogVisible = false"
          >
            OK
          </VBtn>
        </VCardActions>
      </VCard>
      <VBtn
        icon="$close"
        size="large"
        variant="text"
        color="secondary"
        class="dialog-close-btn"
        @click="isConfirmationDialogVisible = false"
      >
        <VIcon>mdi-close</VIcon>
      </VBtn>
    </VDialog>

    <section class="home-masthead">
      <div class="content-wrapper">
        <div class="masthead-content mt-4 mb-2 pl-0 pr-0">
          <div class="title-container">
            <h1 class="title-text">
              Luxury Partners:
            </h1>
            <h2 class="subtitle-text">
              Recommended by RM Members
            </h2>
          </div>
        </div>
      </div>
    </section>
    <VDivider thickness="2" />
    <div
      style="display: flex;"
      class="justify-center mt-5 mb-8"
    >
      <div class="content-wrapper partners-container">
        <VRow class="justify-center">
          <VCol
            v-for="partner in partners"
            :key="partner.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <VCard
              class="partner-card mx-auto"
              elevation="2"
              outlined
            >
              <VCardText class="partner-card-content text-center pa-6">
                <div class="partner-card-body">
                  <div class="logo-container mb-4">
                    <VImg
                      v-if="partner.logoImageId"
                      :src="mediaUrl(partner.logoImageId, 'small')"
                      :alt="partner.name"
                      contain
                      max-height="60"
                      max-width="200"
                      class="mx-auto"
                    />
                    <div
                      v-else
                      class="logo-placeholder"
                    >
                      {{ partner.name }}
                    </div>
                  </div>
                  
                  <p
                    v-if="partner.categoryName"
                    class="category-text mb-2 text-body-2"
                  >
                    {{ partner.categoryName }}
                  </p>
                  
                  <a
                    v-if="partner.url"
                    :href="partner.url"
                    target="_blank"
                    class="partner-link text-primary text-decoration-none d-block mb-3"
                  >
                    Visit Website
                  </a>
                  
                  <p class="recommended-text mb-4 text-body-2">
                    Recommended by {{ partner.memberCount }} member{{ partner.memberCount !== 1 ? 's' : '' }}
                  </p>
                  
                  <p class="partner-description text-body-2 text-center mb-4">
                    {{ truncateDescription(partner.description, 250) }}
                  </p>
                </div>
                
                <div class="partner-card-footer">
                  <VBtn
                    variant="flat"
                    color="primary"
                    size="small"
                    block
                    @click="handleRequestConnection(partner)"
                  >
                    Request connection
                  </VBtn>
                </div>
              </VCardText>
            </VCard>
          </VCol>

          <VCol
            v-if="loadingPartners"
            cols="12"
          >
            <div class="text-center pa-6">
              <VProgressCircular
                indeterminate
                color="primary"
              />
              <p class="mt-4 text-body-2">
                Loading partners...
              </p>
            </div>
          </VCol>
        </VRow>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .masthead-content {
    padding: 0 1rem;
    margin: 1rem 0;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    text-align: center;
    justify-content: center;
    align-items: center;

    @media (min-width: 960px) {
      flex-direction: row;
      gap: 0.5rem;
      align-items: baseline;
    }
  }

  .title-text {
    font-size: 1rem;
    font-weight: 900;
    margin: 0;
    color: var(--ds-text-strong);
  }

  .subtitle-text {
    font-size: 1rem;
    font-weight: 400;
    margin: 0;
    color: var(--ds-text-strong);
  }

  .partners-container {
    max-width: 1200px;
    width: 100%;
    padding: 0 16px;
  }

  .partner-card {
    height: 100%;
    border-radius: 12px;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }
  }

  .partner-card-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
  }

  .partner-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .partner-card-footer {
    margin-top: auto;
  }

  .logo-container {
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .category-text {
    color: var(--ds-text-muted);
    font-weight: 600;
    font-size: 0.95rem;
  }

  .partner-link {
    font-size: 0.875rem;
    word-break: break-all;
    
    &:hover {
      text-decoration: underline !important;
    }
  }

  .recommended-text {
    color: var(--ds-text-muted);
    font-weight: 400;
    font-size: 0.875rem;
  }

  .partner-description {
    line-height: 1.5;
    color: var(--ds-text-secondary);
    min-height: 180px;
    max-height: 180px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 12;
    -webkit-box-orient: vertical;
  }

  .logo-placeholder {
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: var(--ds-text-muted);
  }

  .logo-container-dialog {
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-placeholder-dialog {
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--ds-text-muted);
  }

  .dialog-close-btn {
    position: fixed;
    right: 6px;
    top: 6px;
  }

  .back-button-container {
    padding: 0 4px;
  }

  .back-button {
    padding: 8px 12px;
    color: rgb(51, 51, 51);
    text-decoration: none;
    font-size: 14px;
    transition: background-color 0.2s;
    border-radius: 4px;
    display: inline-flex;
  }

  .back-button:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .back-button svg {
    color: rgb(102, 102, 102);
  }
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: false
</route>

